import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { FaPlus, FaTrashAlt } from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import StatusBadge from "../../generals/StatusBadge";
import LogicContext from "../../Context/LogicContext";
import { jobLocationService } from "../../services/jobLocation.service";
import JobLocationSideScroll from "./JobLocationSideScroll";
import JobLocationOverview from "./JobLocationOverview";
import JobLocationFormModal from "./JobLocationFormModal";

function isActive(record) {
  return Number(record?.statusId) === 1;
}

function jobLocationToastMessage(message) {
  return /job location/i.test(message) ? message : `Job Location: ${message}`;
}

function JobLocationDetailsPage() {
  const [jobLocation, setJobLocation] = useState(null);
  const [jobLocations, setJobLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [formMode, setFormMode] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [error, setError] = useState("");
  const latestRequest = useRef(0);
  const logicContext = useContext(LogicContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { pathname } = useLocation();
  const url = pathname.split("/");
  const basePath = `/${url[1]}/${url[2]}/${url[3] || "job-locations"}`;

  useEffect(() => {
    document.title = "Job Location Details";
  }, []);

  useEffect(() => {
    if (!id) return undefined;
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError("");
    Promise.all([
      jobLocationService.getJobLocation(id),
      jobLocationService.getJobLocations(),
    ])
      .then(([detail, list]) => {
        if (latestRequest.current !== requestId) return;
        setJobLocation(detail);
        setJobLocations(list);
      })
      .catch((loadError) => {
        if (latestRequest.current !== requestId) return;
        setError(
          loadError?.message
            ? jobLocationToastMessage(loadError.message)
            : "Unable to load Job Location details."
        );
        setJobLocation(null);
      })
      .finally(() => {
        if (latestRequest.current === requestId) setLoading(false);
      });
    return () => {
      latestRequest.current += 1;
    };
  }, [id]);

  function viewJobLocationDetails(jobLocationId) {
    navigate(`${basePath}/${jobLocationId}`);
  }

  async function refreshAfterWrite() {
    const [detail, list] = await Promise.all([
      jobLocationService.getJobLocation(id),
      jobLocationService.getJobLocations(),
    ]);
    setJobLocation(detail);
    setJobLocations(list);
  }

  async function refreshListAfterWrite() {
    setJobLocations(await jobLocationService.getJobLocations());
  }

  async function handleEditJobLocation({ name, statusId }) {
    setActionLoading("edit");
    try {
      const result = await jobLocationService.updateJobLocation(id, name, statusId);
      logicContext.showToast(jobLocationToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          jobLocationToastMessage(
            refreshError?.message || "Unable to refresh Job Location details."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  async function handleCreateJobLocation({ name }) {
    setActionLoading("create");
    try {
      const result = await jobLocationService.createJobLocation(name);
      logicContext.showToast(jobLocationToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          jobLocationToastMessage(
            refreshError?.message || "Unable to refresh Job Locations."
          ),
          "error"
        );
      }
      setFormMode("");
    } finally {
      setActionLoading("");
    }
  }

  function handleStatusChange() {
    const action = isActive(jobLocation) ? "deactivate" : "activate";
    setConfirmAction({
      action: "status",
      title: `Confirm ${action} Job Location`,
      message: `Are you sure you want to ${action} the Job Location "${jobLocation.name}"?`,
    });
  }

  async function applyStatusChange() {
    setActionLoading("status");
    try {
      const result = await jobLocationService.setJobLocationStatus(
        id,
        isActive(jobLocation) ? 2 : 1
      );
      logicContext.showToast(jobLocationToastMessage(result.message), "success");
      try {
        await refreshAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          jobLocationToastMessage(
            refreshError?.message || "Unable to refresh Job Location details."
          ),
          "error"
        );
      }
    } catch (statusError) {
      logicContext.showToast(
        jobLocationToastMessage(
          statusError?.message || "Unable to update Job Location status."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function handleDeleteJobLocation() {
    setConfirmAction({
      action: "delete",
      title: "Confirm Delete Job Location",
      message: `The Job Location "${jobLocation.name}" will be permanently deleted. Do you want to continue?`,
    });
  }

  async function applyDeleteJobLocation() {
    setActionLoading("delete");
    try {
      const result = await jobLocationService.deleteJobLocation(id);
      logicContext.showToast(jobLocationToastMessage(result.message), "success");
      try {
        await refreshListAfterWrite();
      } catch (refreshError) {
        logicContext.showToast(
          jobLocationToastMessage(
            refreshError?.message || "Unable to refresh Job Locations."
          ),
          "error"
        );
      }
      navigate(basePath);
    } catch (deleteError) {
      logicContext.showToast(
        jobLocationToastMessage(
          deleteError?.message || "Unable to delete Job Location."
        ),
        "error"
      );
    } finally {
      setActionLoading("");
    }
  }

  function confirmPendingAction() {
    const action = confirmAction?.action;
    setConfirmAction(null);
    if (action === "status") return applyStatusChange();
    if (action === "delete") return applyDeleteJobLocation();
  }

  const actionsDisabled = loading || Boolean(actionLoading) || !jobLocation;

  return (
    <div className="@container">
      <div className="mt-2 flex flex-col @xs:flex-row @xs:items-center @xs:justify-end">
        <div className="mr-6 @xs:mt-2 w-fit flex flex-row items-center px-2.5 py-[2px] border-[1px] border-gray-200 bg-white rounded-full">
          <StatusBadge
            statusId={isActive(jobLocation) ? 1 : "I"}
            statusName={jobLocation?.statusName || "-"}
          />
        </div>
        <div className="mt-2 flex flex-row items-center">
          <Tooltip title="Delete Job Location">
            <button
              onClick={handleDeleteJobLocation}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaTrashAlt className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Edit Job Location">
            <button
              onClick={() => setFormMode("edit")}
              disabled={actionsDisabled}
              className="mr-2 px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <MdEdit className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
          <Tooltip title="Add Job Location">
            <button
              onClick={() => setFormMode("create")}
              disabled={actionsDisabled}
              className="px-3 py-2.5 bg-gray-200 border-[1px] border-gray-300 rounded-md text-white focus:outline-none font-medium hover:bg-gray-100 active:bg-gray-200 transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaPlus className="h-[16px] w-[16px] text-gray-600" />
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          onClick={handleStatusChange}
          disabled={actionsDisabled}
          className="px-4 py-2 text-sm rounded-md bg-gray-200 text-gray-700 font-medium hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isActive(jobLocation) ? "Deactivate Job Location" : "Activate Job Location"}
        </button>
      </div>

      <div className="mt-6 w-full flex flex-col @xl:flex-row">
        <div className="my-1 mx-1 w-full @xl:w-[210px] flex-shrink-0 bg-white">
          <JobLocationSideScroll
            jobLocations={jobLocations}
            viewJobLocationDetails={viewJobLocationDetails}
          />
        </div>
        <div className="mt-10 @xl:mt-0 w-full @xl:ml-4">
          <div className="pb-2 border-b-[1.5px] border-b-gray-200 @xl:px-4 flex flex-row flex-wrap @md:justify-between max-w-3xl">
            <div className="mt-2 mr-2 text-sm font-medium cursor-pointer text-primary">
              Job Location Overview
            </div>
          </div>
          <div className="mt-2 max-auto max-w-6xl text-filterTextColor">
            {loading ? (
              <div className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-8 w-1/2 rounded bg-gray-100" />
                <div className="mt-4 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
                <div className="mt-3 h-5 rounded bg-gray-100" />
              </div>
            ) : null}
            {!loading && error ? (
              <div className="rounded-lg border border-red-100 bg-red-50 p-5 text-sm text-red-700">{error}</div>
            ) : null}
            {!loading && !error && !jobLocation ? (
              <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">
                Job Location not found.
              </div>
            ) : null}
            {!loading && !error && jobLocation ? (
              <JobLocationOverview jobLocation={jobLocation} />
            ) : null}
          </div>
        </div>
      </div>

      {formMode ? (
        <JobLocationFormModal
          mode={formMode}
          initialValues={
            formMode === "edit"
              ? { name: jobLocation?.name, statusId: jobLocation?.statusId }
              : { name: "", statusId: 1 }
          }
          onSubmit={
            formMode === "edit"
              ? handleEditJobLocation
              : handleCreateJobLocation
          }
          onClose={() => setFormMode("")}
        />
      ) : null}

      <Dialog
        open={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        aria-labelledby="job-location-confirm-title"
      >
        <DialogTitle id="job-location-confirm-title">{confirmAction?.title}</DialogTitle>
        <DialogContent>{confirmAction?.message}</DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setConfirmAction(null)}>Cancel Job Location Action</Button>
          <Button
            variant="contained"
            color={confirmAction?.action === "delete" ? "error" : "primary"}
            onClick={confirmPendingAction}
          >
            Confirm Job Location Action
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default JobLocationDetailsPage;
